import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Form,
  Input,
  Select,
  Modal,
  message,
  Tabs,
  Row,
  Col,
  Tooltip
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import commonConfigService, { CommonConfig } from '../../../../../services/commonConfigService';
import { useDict } from '../../../../../hooks/useDict';
import { PAGINATION_CONFIG } from '../../components/PaginationConfig';
import QueryArea from '../../components/QueryArea';
import commonConfigManager from '../../../../../utils/commonConfigManager';
import CarouselManagement from '../Carousel';

const { Option } = Select;

// 配置表单内容组件（支持级联）
interface ConfigFormContentProps {
  form: any;
  configTypeDict: any[];
  configItemDict: any[];
  editingConfig: CommonConfig | null;
  activeTab: string;
}

const ConfigFormContent: React.FC<ConfigFormContentProps> = ({
  form,
  configTypeDict,
  configItemDict,
  editingConfig,
  activeTab
}) => {
  // 监听配置类型字段的变化
  const selectedConfigType = Form.useWatch('configType', form) || activeTab;

  // 根据配置类型获取配置项选项（通过list_class匹配）
  const configKeyOptions = useMemo(() => {
    if (!configItemDict || !selectedConfigType) return [];
    return configItemDict
      .filter((item: any) => item.listClass === selectedConfigType)
      .map((item: any) => ({
        label: item.dictLabel,
        value: item.dictValue
      }));
  }, [configItemDict, selectedConfigType]);

  // 当配置类型变化时，清空配置项（如果当前选择的配置项不在新类型的选项中）
  useEffect(() => {
    const currentConfigKey = form.getFieldValue('configKey');
    if (currentConfigKey && selectedConfigType) {
      const isValidOption = configKeyOptions.some((opt: any) => opt.value === currentConfigKey);
      if (!isValidOption) {
        form.setFieldsValue({ configKey: undefined });
      }
    }
  }, [selectedConfigType, configKeyOptions, form]);

  return (
    <Form 
      form={form} 
      layout="horizontal" 
      labelCol={{ span: 4 }} 
      wrapperCol={{ span: 20 }}
    >
      {/* 第一排：配置类型和配置项 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="configType"
            label="配置类型:"
            rules={[{ required: true, message: '请选择配置类型' }]}
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            labelAlign="left"
          >
            <Select 
              placeholder="请选择配置类型"
              disabled={!!editingConfig}
            >
              {configTypeDict?.map((item: any) => (
                <Option key={item.dictValue} value={item.dictValue}>
                  {item.dictLabel}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="configKey"
            label="配置项:"
            rules={[{ required: true, message: '请选择配置项' }]}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            labelAlign="left"
          >
            <Select 
              placeholder="请选择配置项"
              showSearch
              optionFilterProp="label"
            >
              {configKeyOptions.map((option: any) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* 第二排：配置值 */}
      <Form.Item
        name="configValue"
        label="配置值:"
        rules={[{ required: true, message: '请输入配置值' }]}
        labelAlign="left"
      >
        <Input.TextArea rows={3} placeholder="请输入配置值" />
      </Form.Item>

      {/* 第三排：排序和状态 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="sortOrder"
            label="排序:"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            labelAlign="left"
          >
            <Input type="number" placeholder="请输入排序" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="status"
            label="状态:"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
            labelAlign="left"
          >
            <Select>
              <Option value="1">正常</Option>
              <Option value="0">停用</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* 最后一排：配置描述 */}
      <Form.Item
        name="configDesc"
        label="配置描述:"
        labelAlign="left"
      >
        <Input.TextArea rows={2} placeholder="请输入配置描述" />
      </Form.Item>
    </Form>
  );
};

const CommonConfigManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [configs, setConfigs] = useState<CommonConfig[]>([]);
  const [editingConfig, setEditingConfig] = useState<CommonConfig | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('contact_info');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchParams, setSearchParams] = useState({
    configKey: '',
    configValue: '',
  });

  // 获取字典数据
  const { dictData: configTypeDict } = useDict('common_config_type');
  const { dictData: configItemDict } = useDict('common_config_item');

  // 配置类型映射
  const configTypeMap: Record<string, string> = {
    contact_info: '联系我们',
    website_info: '网站信息',
    record_info: '备案信息',
    display_config: '展示配置'
  };

  // 获取配置类型的中文名称
  const getConfigTypeLabel = (configType?: string): string => {
    if (!configType) return '当前类型';
    return configTypeMap[configType] || configType;
  };

  // 配置类型到配置项的映射关系
  const configTypeToKeysMap: Record<string, string[]> = {
    contact_info: ['office_phone', 'pre_audit_phone', 'contact_email', 'contact_address', 'postal_code'],
    website_info: ['website_title', 'website_subtitle', 'host_unit', 'copyright'],
    record_info: ['website_id', 'icp_record', 'police_record'],
    display_config: ['homepage_carousel', 'platform_intro', 'service_showcase'],
  };

  // 加载配置数据
  const loadConfigs = async (configType?: string, page: number = 1, size: number = 10, search?: { configKey?: string; configValue?: string }) => {
    setLoading(true);
    try {
      const params: any = { 
        current: page, 
        size: size,
        ...(configType && { configType }),
        ...(search?.configKey && { configKey: search.configKey }),
        ...(search?.configValue && { configValue: search.configValue }),
      };
      const response = await commonConfigService.getList(params);
      if (response.code === 200 && response.data) {
        // MyBatis-Plus返回的是Page对象，需要从records中获取数据
        const pageData = response.data.records || response.data;
        const data = Array.isArray(pageData) ? pageData : [];
        setConfigs(data);
        
        // 更新分页信息
        if (response.data.total !== undefined) {
          setPagination({
            current: response.data.current || page,
            pageSize: response.data.size || size,
            total: response.data.total || 0,
          });
        }
      }
    } catch (error) {
      message.error('加载配置失败');
    } finally {
      setLoading(false);
    }
  };

  // 当页签切换时，验证并清空无效的配置项选择
  useEffect(() => {
    if (searchParams.configKey) {
      const allowedKeys = configTypeToKeysMap[activeTab] || [];
      if (!allowedKeys.includes(searchParams.configKey)) {
        setSearchParams(prev => ({ ...prev, configKey: '' }));
      }
    }
  }, [activeTab]);

  useEffect(() => {
    loadConfigs(activeTab, pagination.current, pagination.pageSize, searchParams);
  }, [activeTab, pagination.current, pagination.pageSize, searchParams]);

  // 处理标签页切换
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSelectedRowKeys([]);
    setPagination(prev => ({ ...prev, current: 1 }));
    setSearchParams({ configKey: '', configValue: '' });
  };

  // 搜索
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 重置
  const handleReset = () => {
    setSearchParams({ configKey: '', configValue: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 处理查询参数变化
  const handleSearchParamsChange = (values: Record<string, any>) => {
    setSearchParams({
      configKey: values.configKey || '',
      configValue: values.configValue || '',
    });
  };

  // 打开编辑模态框
  const handleEdit = (config: CommonConfig) => {
    setEditingConfig(config);
    form.setFieldsValue(config);
    setModalVisible(true);
  };

  // 打开新增模态框
  const handleAdd = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({ configType: activeTab, status: '1' });
    setModalVisible(true);
  };

  // 保存配置
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const configType = values.configType || activeTab;
      
      if (editingConfig) {
        await commonConfigService.update({ ...editingConfig, ...values });
        message.success('更新成功');
      } else {
        // 新增时进行查重检查，通过configKey和configType组合查重
        const checkParams: any = {
          current: 1,
          size: 1,
          configKey: values.configKey,
          configType: configType,
          exactMatch: true, // 使用精确匹配
        };
        const checkResponse = await commonConfigService.getList(checkParams);
        
        if (checkResponse.code === 200 && checkResponse.data) {
          const existingRecords = checkResponse.data.records || checkResponse.data;
          const existingData = Array.isArray(existingRecords) ? existingRecords : [];
          
          if (existingData.length > 0) {
            const configKeyLabel = getConfigKeyLabel(values.configKey);
            const configTypeLabel = getConfigTypeLabel(configType);
            message.warning(`配置项"${configKeyLabel}"在"${configTypeLabel}"中已存在，无法重复添加`);
            return;
          }
        }
        
        await commonConfigService.create(values);
        message.success('新增成功');
      }
      
      // 清除对应类型的缓存
      commonConfigManager.clearConfigCache(configType);
      
      // 刷新数据
      await loadConfigs(activeTab, pagination.current, pagination.pageSize, searchParams);
      
      setModalVisible(false);
      // 重置编辑状态
      setEditingConfig(null);
      form.resetFields();
    } catch (error: any) {
      // 如果是后端返回的错误信息，显示具体错误
      if (error?.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('保存失败');
      }
    }
  };

  // 删除配置
  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个配置吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await commonConfigService.remove([id]);
          message.success('删除成功');
          
          // 清除对应类型的缓存
          commonConfigManager.clearConfigCache(activeTab);
          
          // 刷新数据
          await loadConfigs(activeTab, pagination.current, pagination.pageSize, searchParams);
        } catch (error) {
          message.error('删除失败');
        }
      }
    });
  };

  // 批量删除配置
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的配置');
      return;
    }
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 个配置吗？`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await commonConfigService.remove(selectedRowKeys as number[]);
          message.success('批量删除成功');
          setSelectedRowKeys([]);
          
          // 清除对应类型的缓存
          commonConfigManager.clearConfigCache(activeTab);
          
          // 刷新数据
          await loadConfigs(activeTab, pagination.current, pagination.pageSize, searchParams);
        } catch (error) {
          message.error('批量删除失败');
        }
      },
    });
  };

  // 处理分页变化
  const handlePaginationChange = (page: number, size: number) => {
    setPagination(prev => ({ ...prev, current: page, pageSize: size }));
  };


  // 获取配置键的中文名称
  const getConfigKeyLabel = (key: string) => {
    const item = configItemDict?.find((item: any) => item.dictValue === key);
    return item ? item.dictLabel : key;
  };

  // 获取指定配置类型下的配置键选项（根据list_class匹配）
  const getConfigKeyOptionsByType = (configType?: string) => {
    if (!configItemDict || !configType) return [];
    // 根据配置类型的dict_value匹配配置项的list_class
    return configItemDict
      .filter((item: any) => item.listClass === configType)
      .map((item: any) => ({
        label: item.dictLabel,
        value: item.dictValue
      }));
  };

  // 获取当前配置类型下的配置键选项（用于列表和查询区域）
  const getConfigKeyOptions = () => {
    if (!configItemDict) return [];
    const allowedKeys = configTypeToKeysMap[activeTab] || [];
    return configItemDict
      .filter((item: any) => allowedKeys.includes(item.dictValue))
      .map((item: any) => ({
        label: item.dictLabel,
        value: item.dictValue
      }));
  };

  // 查询字段配置（根据当前页签动态过滤）
  const queryFields = useMemo(() => [
    {
      key: 'configKey',
      label: '配置项',
      type: 'select' as const,
      placeholder: '请选择配置项',
      options: getConfigKeyOptions(),
    },
    {
      key: 'configValue',
      label: '配置值',
      type: 'input' as const,
      placeholder: '请输入配置值',
    },
  ], [configItemDict, activeTab]);

  // 表格列定义
  const columns: ColumnsType<CommonConfig> = [
    {
      title: '配置项',
      dataIndex: 'configKey',
      key: 'configKey',
      width: 180,
      render: (text: string) => getConfigKeyLabel(text),
    },
    {
      title: '配置值',
      dataIndex: 'configValue',
      key: 'configValue',
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      align: 'center',
      render: (text: any, record: CommonConfig) => (
        <Space size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button 
            danger 
            size="small" 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id!)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <Card style={{ width: '100%' }}>
        <Tabs 
          activeKey={activeTab} 
          onChange={handleTabChange}
          items={[
            {
              key: 'contact_info',
              label: '联系我们',
              children: (
                <>
                  {/* 统一查询区域 */}
                  <QueryArea
                    fields={queryFields}
                    values={searchParams}
                    onValuesChange={handleSearchParamsChange}
                    onAdd={handleAdd}
                    onBatchDelete={handleBatchDelete}
                    batchDeleteDisabled={selectedRowKeys.length === 0}
                    selectedCount={selectedRowKeys.length}
                    addButtonText="新增配置"
                    batchDeleteButtonText="批量删除"
                  />
                  <Table
                    columns={columns}
                    dataSource={configs}
                    loading={loading}
                    rowKey="id"
                    rowSelection={{
                      selectedRowKeys,
                      onChange: setSelectedRowKeys,
                    }}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      ...PAGINATION_CONFIG.simple,
                      onChange: handlePaginationChange,
                      onShowSizeChange: handlePaginationChange,
                    }}
                    size="small"
                  />
                </>
              ),
            },
            {
              key: 'website_info',
              label: '网站信息',
              children: (
                <>
                  {/* 统一查询区域 */}
                  <QueryArea
                    fields={queryFields}
                    values={searchParams}
                    onValuesChange={handleSearchParamsChange}
                    onAdd={handleAdd}
                    onBatchDelete={handleBatchDelete}
                    batchDeleteDisabled={selectedRowKeys.length === 0}
                    selectedCount={selectedRowKeys.length}
                    addButtonText="新增配置"
                    batchDeleteButtonText="批量删除"
                  />
                  <Table
                    columns={columns}
                    dataSource={configs}
                    loading={loading}
                    rowKey="id"
                    rowSelection={{
                      selectedRowKeys,
                      onChange: setSelectedRowKeys,
                    }}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      ...PAGINATION_CONFIG.simple,
                      onChange: handlePaginationChange,
                      onShowSizeChange: handlePaginationChange,
                    }}
                    size="small"
                  />
                </>
              ),
            },
            {
              key: 'record_info',
              label: '备案信息',
              children: (
                <>
                  {/* 统一查询区域 */}
                  <QueryArea
                    fields={queryFields}
                    values={searchParams}
                    onValuesChange={handleSearchParamsChange}
                    onAdd={handleAdd}
                    onBatchDelete={handleBatchDelete}
                    batchDeleteDisabled={selectedRowKeys.length === 0}
                    selectedCount={selectedRowKeys.length}
                    addButtonText="新增配置"
                    batchDeleteButtonText="批量删除"
                  />
                  <Table
                    columns={columns}
                    dataSource={configs}
                    loading={loading}
                    rowKey="id"
                    rowSelection={{
                      selectedRowKeys,
                      onChange: setSelectedRowKeys,
                    }}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      ...PAGINATION_CONFIG.simple,
                      onChange: handlePaginationChange,
                      onShowSizeChange: handlePaginationChange,
                    }}
                    size="small"
                  />
                </>
              ),
            },
            {
              key: 'display_config',
              label: '展示配置',
              children: <CarouselManagement />,
            },
          ]}
        />
      </Card>

      {/* 编辑模态框 */}
      <Modal
        title={editingConfig ? '编辑配置' : '新增配置'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <ConfigFormContent
          form={form}
          configTypeDict={configTypeDict}
          configItemDict={configItemDict}
          editingConfig={editingConfig}
          activeTab={activeTab}
        />
      </Modal>
    </div>
  );
};

export default CommonConfigManagement;
